import React, { useState, useEffect } from "react";
import axios from "axios";
import { GripVertical, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API } from "@/context";

export function ArtworkCard({ artwork, onUpdate, stageColor, collapsed }) {
  const [quantity, setQuantity] = useState(artwork.quantity || 0);
  const [printQuantity, setPrintQuantity] = useState(artwork.print_quantity || 0);
  const [isStarted, setIsStarted] = useState(artwork.is_started || false);
  const [isFinished, setIsFinished] = useState(artwork.is_finished || false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  useEffect(() => { setIsCollapsed(collapsed); }, [collapsed]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/artworks/${artwork.id}`, { quantity, print_quantity: printQuantity });
      toast.success("Saved successfully");
      onUpdate();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusToggle = async (field, value) => {
    try {
      const update = {};
      if (field === "is_started") {
        update.is_started = value;
        if (value) update.is_finished = false;
        setIsStarted(value);
        if (value) setIsFinished(false);
      } else {
        update.is_finished = value;
        if (value) update.is_started = false;
        setIsFinished(value);
        if (value) setIsStarted(false);
      }
      await axios.put(`${API}/artworks/${artwork.id}`, update);
      onUpdate();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const cardBg = isFinished ? "#DCFCE7" : isStarted ? "#FEF9C3" : "#FEE2E2";
  const cardBorder = isFinished ? "#16A34A" : isStarted ? "#CA8A04" : "#DC2626";
  const statusDot = isFinished ? "bg-green-500" : isStarted ? "bg-yellow-500" : "bg-red-400";

  return (
    <div
      data-testid={`artwork-card-${artwork.id}`}
      className="border-2 p-2.5 shadow-sm hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 relative"
      style={{ backgroundColor: cardBg, borderColor: cardBorder }}
    >
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setIsCollapsed(!isCollapsed)}
        data-testid={`card-toggle-${artwork.id}`}
      >
        <div className="flex items-center gap-1.5">
          {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          <span className="text-[11px] font-bold tracking-wide uppercase">{artwork.type}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${statusDot}`} />
          <GripVertical className="h-3 w-3 text-muted-foreground cursor-move" onClick={(e) => e.stopPropagation()} />
        </div>
      </div>

      {!isCollapsed && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium uppercase text-muted-foreground block mb-0.5">Qty</label>
              <Input type="number" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 0)} onFocus={(e) => e.target.select()} className="h-7 text-xs rounded-none border-2 bg-white" min="0" data-testid={`quantity-input-${artwork.id}`} />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase text-muted-foreground block mb-0.5">Print Qty</label>
              <Input type="number" value={printQuantity} onChange={(e) => setPrintQuantity(parseInt(e.target.value) || 0)} onFocus={(e) => e.target.select()} className="h-7 text-xs rounded-none border-2 bg-white" min="0" data-testid={`print-quantity-input-${artwork.id}`} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 cursor-pointer" data-testid={`started-checkbox-${artwork.id}`}>
              <input type="checkbox" checked={isStarted} onChange={(e) => handleStatusToggle("is_started", e.target.checked)} className="w-3.5 h-3.5 accent-yellow-600 cursor-pointer" />
              <span className="text-[10px] font-medium uppercase">Started</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer" data-testid={`finished-checkbox-${artwork.id}`}>
              <input type="checkbox" checked={isFinished} onChange={(e) => handleStatusToggle("is_finished", e.target.checked)} className="w-3.5 h-3.5 accent-green-600 cursor-pointer" />
              <span className="text-[10px] font-medium uppercase">Finished</span>
            </label>
          </div>
          <Button onClick={handleSave} disabled={isSaving} className="w-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none h-7 text-xs" data-testid={`save-btn-${artwork.id}`}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}
